import React, { PropsWithChildren, useEffect, useRef, useState } from "react";
import Modal from "../index";
import Input from "components/Input";
import Text from "components/Text";
import { Button } from "components/Button";
import AutoComplete from "components/AutoComplete";
import { Column } from "components/Layout/Grid";
import Loading from "components/Loading";
import searchIcon from "img/search-icon.svg";
import cleanBtn from "img/icon-clean-search.svg";
import UserService from "services/user.service";
import { UserType } from "models/User";
import { isEligibleUserSuggestion } from "pages/Quotes/ManagementPanel/userSearchExclusions";
import ptBR from "i18n/pt-BR.json";
import {
  SearchFieldWrapper,
  SearchFieldLabel,
  SearchFieldsRow,
  SearchInputContainer,
  SearchColumnWrapper,
  UserSearchContainer,
  SelectedUserChip,
  TableContentWrapper,
} from "./styles";
import { IManagementPanelModalCardProps, IManagementPanelModalCardUserSuggestion } from "./interface";

const USER_SEARCH_MIN_LENGTH = 3;

interface IUserAutoCompleteApiItem {
  id?: string;
  fullName?: string;
  email?: string;
  enabled?: boolean;
}

const UserSuggestionContent: React.FC<{ item: IUserAutoCompleteApiItem }> = ({ item }) => (
  <>
    <Text className="text-left color-blue-gray pt-1" size={13}>
      {item?.fullName || item?.email}
    </Text>
    <Text className="text-left pb-1" size={10}>
      {item?.email}
    </Text>
  </>
);

const ManagementPanelModalCard: React.FC<PropsWithChildren<IManagementPanelModalCardProps>> = (props) => {
  const {
    title,
    searchFieldLabel,
    width = "1224px",
    height = "864px",
    searchValue = "",
    onSearchSubmit,
    onCloseAction,
    onOpen,
    showCloseButton = true,
    children,
    searchPlaceholder = "Incie sua busca",
    minWidth,
    borderRadius = "8px",
    onKeyDown,
    isLoading = false,
    userSearchLabel,
    userSearchPlaceholder,
    selectedUserLabel = "",
    onUserSelect,
    onUserClear,
  } = props;

  const inputRef = useRef<HTMLInputElement>(null);
  const [inputResetKey, setInputResetKey] = useState(0);
  const _userService = new UserService();

  const [userSearchTerm, setUserSearchTerm] = useState("");
  const [showUserAutoComplete, setShowUserAutoComplete] = useState(false);
  const [userSearchError, setUserSearchError] = useState(false);
  const [appliedUser, setAppliedUser] = useState<IManagementPanelModalCardUserSuggestion | null>(
    selectedUserLabel ? { email: selectedUserLabel } : null
  );

  const userSearchEnabled = !!onUserSelect;

  useEffect(() => {
    onOpen?.();
  }, []);

  function handleKeyDown(key: string, e: React.KeyboardEvent) {
    if (key === "Enter") {
      const value = inputRef.current?.value ?? "";
      onSearchSubmit?.(value);
    } 
    onKeyDown?.(key);
  }

  async function requestUserAutoComplete(value: string) {
    try {
      const response = await _userService.autoComplete(value, UserType.Employee);

      if (Array.isArray(response)) {
        setUserSearchError(true);
        return { data: [] };
      }

      setUserSearchError(false);
      const rawList: IUserAutoCompleteApiItem[] = Array.isArray(response?.data) ? response.data : [];
      const eligibleList = rawList.filter((item) => isEligibleUserSuggestion(item));

      return { data: eligibleList };
    } catch {
      setUserSearchError(true);
      return { data: [] };
    }
  }

  function handleUserSelectOption(item: IUserAutoCompleteApiItem) {
    const email = item?.email ?? "";
    if (!email) return;

    setAppliedUser({ email, fullName: item?.fullName });
    setUserSearchTerm("");
    setShowUserAutoComplete(false);
    setUserSearchError(false);
    onUserSelect?.({ email, fullName: item?.fullName });
  }

  function handleUserSearchChange(value: string) {
    setUserSearchTerm(value);
    setShowUserAutoComplete(true);
  }

  function handleUserClear() {
    setAppliedUser(null);
    setUserSearchTerm("");
    setShowUserAutoComplete(false);
    setUserSearchError(false);
    onUserClear?.();
  }

  function handleUserInputBlur(relatedTargetId?: string) {
    const exceptionButtonAutoCompleteId = "auto-complete-header";
    if (relatedTargetId === exceptionButtonAutoCompleteId) return;

    setShowUserAutoComplete(false);
  }

  function renderExistingSearchInput() {
    return (
      <Input
        key={inputResetKey}
        ref={inputRef}
        type="text"
        defaultValue=""
        placeholder={searchPlaceholder}
        className="w-100"
        icon={searchIcon}
        iconStyle={{ top: 2, right: 2, bottom: 2, borderRadius: 6, pointerEvents: "none" }}
        onKeyDown={(e) => handleKeyDown(e.key, e)}
      />
    );
  }

  return (
    <Modal
      title={title}
      width={width}
      height={height}
      minWidth={minWidth}
      borderRadius={borderRadius}
      showCloseButton={showCloseButton}
      onCloseAction={onCloseAction}
    >
      <Column className="flex-1 overflow-hidden d-flex flex-column" style={{ minHeight: 0, position: 'relative' }}>
        {isLoading && (
          <div style={{
            position: 'absolute',
            top: 0, right: 0, bottom: 0, left: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(255,255,255,0.85)',
            backdropFilter: 'blur(3px)',
            zIndex: 10,
          }}>
            <Loading onlySpinner size={45} />
          </div>
        )}
        <SearchFieldWrapper>
          <SearchFieldLabel>
            <hr style={{ width: '100%' }} />
            {!userSearchEnabled && (
              <Text size={14} className="color-blue-gray font-medium">
                {searchFieldLabel}
              </Text>
            )}
          </SearchFieldLabel>

          {userSearchEnabled ? (
            <SearchFieldsRow>
              <SearchColumnWrapper>
                <Text size={14} className="color-blue-gray font-medium mb-1">
                  {searchFieldLabel}
                </Text>
                <SearchInputContainer>
                  {renderExistingSearchInput()}
                </SearchInputContainer>
              </SearchColumnWrapper>

              <UserSearchContainer>
                <Text size={14} className="color-blue-gray font-medium mb-1">
                  {userSearchLabel ?? ptBR.managementPanel.userSearch.label}
                </Text>

                {appliedUser ? (
                  <SelectedUserChip className="bg-gray">
                    <Button
                      className="overflow-x-hidden"
                      width="auto"
                      testId="management-panel-user-search-selected"
                    >
                      <Text
                        size={13}
                        className="color-primary"
                        showTip
                        showTipContent={appliedUser.email}
                      >
                        {appliedUser.fullName || appliedUser.email}
                      </Text>
                    </Button>
                    <Button
                      className="rounded-full bg-primary"
                      onClick={handleUserClear}
                      style={{ width: 19, height: 19, flexShrink: 0 }}
                      testId="management-panel-user-search-clear"
                    >
                      <Text className="color-white font-bold" size={12}>
                        &times;
                      </Text>
                    </Button>
                  </SelectedUserChip>
                ) : (
                  <>
                    <AutoComplete
                      show={showUserAutoComplete}
                      request={requestUserAutoComplete}
                      onSelect={handleUserSelectOption}
                      content={UserSuggestionContent}
                      minLengthSearch={USER_SEARCH_MIN_LENGTH}
                    >
                      <Input
                        data-testid="management-panel-user-search-input"
                        type="text"
                        placeholder={userSearchPlaceholder ?? ptBR.managementPanel.userSearch.placeholder}
                        className="w-100"
                        value={userSearchTerm}
                        icon={userSearchTerm ? cleanBtn : searchIcon}
                        iconStyle={{ top: 2, right: 2, bottom: 2, borderRadius: 6 }}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleUserSearchChange(e.target.value)}
                        onFocus={() => setShowUserAutoComplete(true)}
                        onBlur={(e: React.FocusEvent<HTMLInputElement>) => handleUserInputBlur((e.relatedTarget as HTMLElement)?.id)}
                        onClickIcon={handleUserClear}
                      />
                    </AutoComplete>
                    {userSearchError && (
                      <Text size={12} className="color-danger mt-1">
                        {ptBR.managementPanel.userSearch.autoCompleteError}
                      </Text>
                    )}
                  </>
                )}
              </UserSearchContainer>
            </SearchFieldsRow>
          ) : (
            <SearchInputContainer>
              {renderExistingSearchInput()}
            </SearchInputContainer>
          )}
        </SearchFieldWrapper>

        {children && (
          <TableContentWrapper>
            {children}
          </TableContentWrapper>
        )}
      </Column>
    </Modal>
  );
};

export default ManagementPanelModalCard;
